// cSpell:ignore Provisies Scenariolaan
// The cards of the wizard's "Provisies" step (Lewis, 2026-10-16): one card per
// unit, headed by the unit's full address like the cost choice's cards, so it
// is clear which tenant belongs to which property. The units in the order of
// their box numbers ("bus 2" before "bus 10"), the leases of a unit in the
// order they started, and the leases whose unit is not known in a last card.

import { compareUnitLabels } from "./buildingSettlement.utils";

export interface IProvisionCard<TLine> {
  key: string;
  // The unit of the card; absent for the card of leases whose unit is not
  // known.
  unitId?: string;
  label: string;
  lines: TLine[];
}

interface IProvisionCardLine {
  contract: { id: string; unitId?: string; startDate: string };
}

// Leases in the order they started; the lease id keeps two that started on
// the same day in a stable order.
export const byLeaseStart = <TLine extends IProvisionCardLine>(
  a: TLine,
  b: TLine,
): number =>
  a.contract.startDate.localeCompare(b.contract.startDate) ||
  a.contract.id.localeCompare(b.contract.id);

export const UNKNOWN_UNIT_CARD_KEY = "unit:unknown";

export const provisionCards = <TLine extends IProvisionCardLine>(params: {
  lines: TLine[];
  // A unit's full address ("Scenariolaan 1 bus 3, 9000 Gent"); undefined when
  // the wizard does not know the unit, which puts its leases in the last card.
  unitLabelOf: (unitId: string) => string | undefined;
  // What the units are ordered by ("bus 2"); their label when not given.
  unitSortLabelOf?: (unitId: string) => string | undefined;
  // The title of the last card, for the leases whose unit is not known.
  unknownUnitTitle: string;
}): IProvisionCard<TLine>[] => {
  const { lines, unitLabelOf, unknownUnitTitle } = params;
  const unitSortLabelOf = params.unitSortLabelOf ?? unitLabelOf;
  const byUnit = new Map<string, TLine[]>();
  const unknown: TLine[] = [];
  for (const line of lines) {
    const unitId = line.contract.unitId ? String(line.contract.unitId) : "";
    if (!unitId || unitLabelOf(unitId) === undefined) {
      unknown.push(line);
      continue;
    }
    byUnit.set(unitId, [...(byUnit.get(unitId) ?? []), line]);
  }
  const labelOf = (unitId: string) => unitLabelOf(unitId) ?? unitId;
  const sortLabelOf = (unitId: string) =>
    unitSortLabelOf(unitId) ?? labelOf(unitId);
  const units = [...byUnit.entries()]
    .sort(
      ([a], [b]) =>
        compareUnitLabels(sortLabelOf(a), sortLabelOf(b)) ||
        compareUnitLabels(labelOf(a), labelOf(b)) ||
        a.localeCompare(b),
    )
    .map(([unitId, unitLines]) => ({
      key: `unit:${unitId}`,
      unitId,
      label: labelOf(unitId),
      lines: [...unitLines].sort(byLeaseStart),
    }));
  return [
    ...units,
    ...(unknown.length > 0
      ? [
          {
            key: UNKNOWN_UNIT_CARD_KEY,
            label: unknownUnitTitle,
            lines: [...unknown].sort(byLeaseStart),
          },
        ]
      : []),
  ];
};
